/**
 * React hook driving the app-owned, in-app registered auth flow (FR-2.11, roadmap 43)
 * over the pure {@link ./auth} helpers.
 *
 * Replaces the M7.2 Cognito Hosted-UI redirect (`?code=` callback + PKCE) with an
 * in-app state machine: a screen (`signIn | signUp | forgot | reset`), a busy/error/
 * notice status, and one action per edge. **Registration is server-mediated** —
 * `startSignUp` posts to the control plane (which admin-creates the account, because
 * Cognito self-signup is force-disabled by Epoxy) and then signs the user in via SRP,
 * so there is NO emailed-code verification step. Sign-in / forgot / reset drive
 * Cognito directly. On a successful sign-in the ID token is posted to `/account/claim`
 * (the unchanged guest→registered seam) and the app resumes exactly where the M7.2
 * callback did: a brand-new registered user with no birth year lands on `/profile`
 * (FR-2.8), everyone else resumes their stashed `returnTo`. The resume is a full-
 * document navigation (like the old redirect return), so every page re-reads the
 * freshly persisted identity and auto-joins.
 *
 * Degrade (D-43.9): when Cognito isn't configured (`/api/config.auth` null) the hook
 * reports `enabled: false` and renders no auth UI — guests are never blocked.
 *
 * This orchestration glue can't run under jsdom (SRP + a full-page resume need a real
 * browser + live pool); it is excluded from coverage. Its building blocks (the auth
 * wrappers, the claim, the post-sign-in routing) are unit-tested in `auth.ts`, the
 * screens by component tests, and the deployed sign-in→claim path by the CD e2e gate.
 */

import { useCallback, useEffect, useState } from 'react';
import type { DateOfBirth } from '@jeopardy/shared';
import { fetchRuntimeConfig, type CognitoClientConfig } from './config-api';
import {
  claimAccount,
  clearIdentity,
  confirmReset,
  forgotPassword,
  loadIdentity,
  register,
  resolvePostSignInTarget,
  signIn as cognitoSignIn,
  stashReturnTo,
  updateBirthYear,
  type Identity,
} from './auth';
import { mapAuthError } from '../ui/auth/errors';

/** The auth panel's current screen. */
export type AuthScreen = 'signIn' | 'signUp' | 'forgot' | 'reset';

export interface AuthState {
  /** True once we know registered sign-in is configured (Cognito present). */
  enabled: boolean;
  /** The current identity (guest or registered), or null before it resolves. */
  identity: Identity | null;
  /** Whether the auth panel is open. */
  open: boolean;
  /** The screen the panel is showing. */
  screen: AuthScreen;
  /** True while an action is in flight. */
  busy: boolean;
  /** Friendly error copy for the current screen, if any. */
  error: string | null;
  /** A transient positive notice (e.g. "Password reset — sign in"), if any. */
  notice: string | null;
  /** Username carried across screens (forgot→reset, reset→sign-in). */
  pendingUsername: string;

  /** Open the panel at `screen` (default sign-in), stashing where to resume. */
  openPanel: (screen?: AuthScreen) => void;
  /** Close the panel and reset transient state. */
  closePanel: () => void;
  /** Switch screens within the panel. */
  goTo: (screen: AuthScreen) => void;

  startSignUp: (
    username: string,
    email: string,
    password: string,
    dateOfBirth: DateOfBirth,
  ) => void;
  signIn: (username: string, password: string) => void;
  forgot: (username: string) => void;
  reset: (code: string, newPassword: string) => void;
  signOut: () => void;
}

export function useAuth(): AuthState {
  const [cfg, setCfg] = useState<CognitoClientConfig | null>(null);
  const [identity, setIdentity] = useState<Identity | null>(() => loadIdentity());
  const [open, setOpen] = useState(false);
  const [screen, setScreen] = useState<AuthScreen>('signIn');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingUsername, setPendingUsername] = useState('');

  // Learn whether registered sign-in is available (guest-only degrade otherwise).
  useEffect(() => {
    let live = true;
    void fetchRuntimeConfig().then((config) => {
      if (live) setCfg(config.auth?.cognito ?? null);
    });
    return () => {
      live = false;
    };
  }, []);

  /**
   * Post the Cognito ID token to the unchanged claim seam, then resume (full load).
   * When `birthYearToSet` is given (the sign-up path — FR-14.1/D-9), reconcile the birth
   * year captured at registration onto the profile via the existing tested endpoint, so a
   * new account doesn't get re-prompted for it. A failed reconcile is non-fatal — the
   * existing `BirthYearPrompt` backstops it — so it never blocks landing the user.
   */
  const completeSignIn = useCallback(
    async (idToken: string, birthYearToSet?: number): Promise<void> => {
      const merged = await claimAccount(idToken);
      let landed = merged;
      if (birthYearToSet !== undefined && !merged.hasBirthYear) {
        try {
          await updateBirthYear(birthYearToSet, merged.guestToken);
          landed = { ...merged, hasBirthYear: true };
        } catch {
          // Non-fatal: leave hasBirthYear false so BirthYearPrompt can pick it up later.
        }
      }
      // Full-document navigation (like the M7.2 redirect return) so every page re-reads
      // the persisted identity and a registered player auto-joins under their username.
      window.location.assign(resolvePostSignInTarget(landed));
    },
    [],
  );

  const openPanel = useCallback((next: AuthScreen = 'signIn') => {
    // Remember where to resume after the flow (survives the full-load resume).
    stashReturnTo(window.location.pathname + window.location.search);
    setScreen(next);
    setError(null);
    setNotice(null);
    setOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
    setBusy(false);
    setError(null);
    setNotice(null);
    // Drop any carried username (memory-only) so reopening the panel starts clean.
    setPendingUsername('');
  }, []);

  const goTo = useCallback((next: AuthScreen) => {
    setScreen(next);
    setError(null);
    setNotice(null);
  }, []);

  /** Run an async auth action with shared busy/error handling. */
  const run = useCallback(async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    // Clear any prior positive notice so an error never renders alongside a stale
    // message; a successful action sets its own notice after.
    setNotice(null);
    try {
      await fn();
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setBusy(false);
    }
  }, []);

  const startSignUp = useCallback(
    (username: string, email: string, password: string, dateOfBirth: DateOfBirth) => {
      if (!cfg) return;
      void run(async () => {
        // Server-mediated: the control plane admin-creates the account (email marked
        // verified) AFTER passing the 13+ age gate (FR-14.1), then we sign the user in
        // via SRP and claim — no email-code step. The birth year captured here is
        // reconciled onto the profile during claim (D-9) so it isn't asked for twice.
        await register(username, email, password, dateOfBirth);
        const { idToken } = await cognitoSignIn(cfg, username, password);
        await completeSignIn(idToken, dateOfBirth.year);
      });
    },
    [cfg, run, completeSignIn],
  );

  const signIn = useCallback(
    (username: string, password: string) => {
      if (!cfg) return;
      void run(async () => {
        const { idToken } = await cognitoSignIn(cfg, username, password);
        await completeSignIn(idToken);
      });
    },
    [cfg, run, completeSignIn],
  );

  const forgot = useCallback(
    (username: string) => {
      if (!cfg) return;
      void run(async () => {
        await forgotPassword(cfg, username);
        setPendingUsername(username);
        setScreen('reset');
        setNotice('We emailed a reset code to your account email.');
      });
    },
    [cfg, run],
  );

  const reset = useCallback(
    (code: string, newPassword: string) => {
      if (!cfg) return;
      void run(async () => {
        await confirmReset(cfg, pendingUsername, code, newPassword);
        setScreen('signIn');
        setNotice('Password reset — sign in with your new password.');
      });
    },
    [cfg, pendingUsername, run],
  );

  const signOut = useCallback(() => {
    // No Hosted-UI/Cognito session is persisted (D-43.6), so a local clear is enough.
    clearIdentity();
    setIdentity(null);
  }, []);

  return {
    enabled: cfg !== null,
    identity,
    open,
    screen,
    busy,
    error,
    notice,
    pendingUsername,
    openPanel,
    closePanel,
    goTo,
    startSignUp,
    signIn,
    forgot,
    reset,
    signOut,
  };
}
